import { useMemo } from "react";
import { useSelector } from "react-redux";
import type { LodgingPrice } from "@b2bportal/lodging-api";
import { usePriceFormatterContext } from "@hopper-b2b/contexts";
import { I18nMarkup, Trans, useI18nContext } from "@hopper-b2b/i18n";
import { getTotalNights } from "@hopper-b2b/lodging-availability";
import { useRenderLodgingStrikethroughPrice } from "@hopper-b2b/utilities";
import { Typography } from "@material-ui/core";
import { Skeleton } from "@material-ui/lab";
import clsx from "clsx";
import { LodgingCardOriginalPrice } from "../LodgingCardOriginalPrice";
import styles from "./LodgingCardPrice.module.scss";

interface Props {
  price: LodgingPrice;
  className?: string;
  loading?: boolean;
}

export function LodgingCardPrice({ price, className, loading = false }: Props) {
  const { brand } = useI18nContext();

  const { formatPrice } = usePriceFormatterContext();
  const renderLodgingStrikethroughPrice = useRenderLodgingStrikethroughPrice();
  const nights = useSelector(getTotalNights);

  const finalPrice = useMemo(
    () => price.nightlyPriceTaxesAndFeesIncluded ?? price.nightlyPrice,
    [price]
  );
  const originalPrice = useMemo(
    () => price.nightlyDiscountAwareTaxesAndFeesIncluded?.priceWithNoDiscounts,
    [price]
  );
  const totalPrice = useMemo(
    () =>
      price.totalDiscountAwareTaxesAndFeesIncluded?.priceWithAllDiscounts ??
      price.totalPrice,
    [price]
  );

  return price ? (
    <Typography
      variant="h2"
      className={clsx(styles.Price, className, {
        strikeThrough:
          renderLodgingStrikethroughPrice &&
          originalPrice?.fiat?.value > finalPrice?.fiat?.value,
      })}
      style={{ lineHeight: "100%" }}
    >
      {loading ? (
        <>
          <Skeleton width={116} variant="text" />
          <Skeleton width={116} variant="text" />
        </>
      ) : (
        <>
          <div className="price-per-night">
            {renderLodgingStrikethroughPrice &&
            originalPrice?.fiat?.value > finalPrice?.fiat?.value ? (
              <LodgingCardOriginalPrice
                originalPrice={originalPrice}
                finalPrice={finalPrice}
              />
            ) : null}
            <I18nMarkup
              tKey={"pricePerNight"}
              replacements={{
                price: formatPrice(finalPrice, {
                  maximumFractionDigits: brand?.lodgingCardPriceRounding || 0,
                  minimumFractionDigits: brand?.lodgingCardPriceRounding || 0,
                  roundingMode: "ceil",
                }),
              }}
            />
          </div>
          <Typography variant="body1">
            <span className="total-price">
              <Trans
                i18nKey={"fromPriceForNightsTaxesIncluded"}
                values={{
                  price: formatPrice(totalPrice, {
                    maximumFractionDigits: brand?.lodgingCardPriceRounding || 0,
                    minimumFractionDigits: brand?.lodgingCardPriceRounding || 0,
                    roundingMode: "ceil",
                  }),
                  count: nights,
                }}
                components={[<strong className="value" />]}
              />
            </span>
            <span className={clsx("total-price", "compact")}>
              <Trans
                i18nKey={"priceForNights"}
                values={{
                  price: formatPrice(totalPrice, {
                    maximumFractionDigits: brand?.lodgingCardPriceRounding || 0,
                    minimumFractionDigits: brand?.lodgingCardPriceRounding || 0,
                    roundingMode: "ceil",
                  }),
                  count: nights,
                }}
                components={[<strong className="value" />]}
              />
            </span>
          </Typography>
        </>
      )}
    </Typography>
  ) : null;
}
